<template>
  <div
    class="fnode"
    :class="{
      'selected': isSelected,
      ['fnode-' + node.type]: true,
      'fnode-nested': depth > 0
    }"
    @click.stop="$emit('select-node', nodeId)"
  >
    <!-- Head -->
    <div class="fnode-head">
      <span v-if="depth === 0" class="node-drag-handle" @click.stop>
        <i class="bx bx-grid-vertical"></i>
      </span>
      <div class="fnode-icon" :style="{ background: getColor(node.kind) }">
        <i :class="getIcon(node.kind)"></i>
      </div>
      <div class="fnode-info">
        <span class="fnode-name">{{ node.label }}</span>
        <span class="fnode-type">{{ getTypeLabel(node.type) }}</span>
      </div>
      <button class="btn-node-action" @click.stop="$emit('remove-node', nodeId)" title="Remover">
        <i class="bx bx-trash"></i>
      </button>
    </div>

    <!-- Body - config preview -->
    <div class="fnode-body" v-if="configItems.length > 0">
      <div class="fnode-config-items">
        <span v-for="(item, i) in configItems" :key="i" class="cfg-pill">
          {{ item }}
        </span>
      </div>
    </div>

    <!-- Branches (recursive) — condition (sim/nao) e wa_wait_reply (um ramo por botao + timeout) -->
    <div class="fnode-branches" v-if="branchDefs.length">
      <div class="fbranch" :class="'fbranch-' + branch.key" v-for="branch in branchDefs" :key="branch.key">
        <div class="fbranch-head">
          <i :class="branch.icon"></i>
          <span>{{ branch.label }}</span>
          <span class="fbranch-count" v-if="childrenOf(branch.key).length">({{ childrenOf(branch.key).length }})</span>
        </div>
        <div class="fbranch-nodes">
          <JourneyNodeCard
            v-for="child in childrenOf(branch.key)"
            :key="child.id || child._tempId"
            :node="child"
            :all-nodes="allNodes"
            :selected-node-id="selectedNodeId"
            :email-templates="emailTemplates"
            :wa-templates="waTemplates"
            :statuses="statuses"
            :groups="groups"
            :tags="tags"
            :forms="forms"
            :journeys-list="journeysList"
            :follow-up-rules="followUpRules"
            :depth="depth + 1"
            @select-node="$emit('select-node', $event)"
            @remove-node="$emit('remove-node', $event)"
            @add-to-branch="$emit('add-to-branch', $event)"
          />
          <div
            class="fbranch-add"
            @click.stop="$emit('add-to-branch', { parentNodeId: nodeId, branchType: branch.key })"
          >
            <i class="bx" :class="childrenOf(branch.key).length === 0 ? 'bx-plus-circle' : 'bx-plus'"></i>
            <span v-if="childrenOf(branch.key).length === 0">Adicionar acao</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats footer -->
    <div class="fnode-stats" v-if="depth === 0">
      <div class="fstat fstat-waiting">
        <span class="fstat-emoji">&#9203;</span>
        <span class="fstat-value">{{ waiting }}</span>
        <span class="fstat-label">aguardando</span>
      </div>
      <div class="fstat fstat-passed">
        <span class="fstat-emoji">&#9989;</span>
        <span class="fstat-value">{{ passed }}</span>
        <span class="fstat-label">passaram</span>
      </div>
    </div>
  </div>
</template>

<script>
import { getNodeColor, getNodeIcon } from '../nodeLibrary'

export default {
  name: 'JourneyNodeCard',
  props: {
    node: { type: Object, required: true },
    allNodes: { type: Array, default: () => [] },
    selectedNodeId: { default: null },
    emailTemplates: { type: Array, default: () => [] },
    waTemplates: { type: Array, default: () => [] },
    statuses: { type: Array, default: () => [] },
    groups: { type: Array, default: () => [] },
    tags: { type: Array, default: () => [] },
    forms: { type: Array, default: () => [] },
    journeysList: { type: Array, default: () => [] },
    followUpRules: { type: Array, default: () => [] },
    depth: { type: Number, default: 0 }
  },
  computed: {
    nodeId () {
      return this.node.id || this.node._tempId
    },
    isSelected () {
      return this.selectedNodeId === this.nodeId
    },
    branchDefs () {
      // Node interativo do WhatsApp: um ramo por botao configurado + o ramo de timeout.
      if (this.node.kind === 'wa_wait_reply') {
        const buttons = (this.node.config && this.node.config.buttons) || []
        const defs = buttons
          .filter(b => b.branch)
          .map(b => ({ key: b.branch, label: b.label || b.branch, icon: 'bx bx-radio-circle-marked' }))
        defs.push({ key: 'timeout', label: 'Sem resposta', icon: 'bx bx-time' })
        return defs
      }
      // Condicoes classicas: Sim / Nao.
      if (this.node.type === 'condition') {
        return [
          { key: 'yes', label: 'Sim', icon: 'bx bx-check-circle' },
          { key: 'no', label: 'Nao', icon: 'bx bx-x-circle' }
        ]
      }
      return []
    },
    configItems () {
      return this.getNodeConfigItems(this.node)
    },
    waiting () {
      const stats = this.node.stats_cache || {}
      return (stats.entered || 0) - (stats.completed || 0)
    },
    passed () {
      const stats = this.node.stats_cache || {}
      return stats.completed || 0
    }
  },
  methods: {
    childrenOf (branchKey) {
      return this.allNodes.filter(n => n.parent_node_id === this.nodeId && n.branch_type === branchKey)
    },
    getColor (kind) {
      return getNodeColor(kind)
    },
    getIcon (kind) {
      return getNodeIcon(kind)
    },
    getTypeLabel (type) {
      const labels = { trigger: 'Gatilho', delay: 'Tempo', action: 'Acao', condition: 'Condicao', control: 'Controle' }
      return labels[type] || type
    },
    unitLabel (unit) {
      const labels = { minutes: 'minutos', hours: 'horas', days: 'dias', weeks: 'semanas' }
      return labels[unit] || unit
    },
    getNodeConfigItems (node) {
      const config = node.config || {}
      const items = []
      switch (node.kind) {
        case 'registration':
        case 'checkin': {
          const gIds = config.group_ids || (config.group_id ? [config.group_id] : [])
          if (gIds.length > 0) {
            const names = gIds.map(id => {
              const g = this.groups.find(x => x.id === id)
              return g ? g.name : 'Grupo #' + id
            })
            items.push(names.join(', '))
          } else {
            items.push('Todos os grupos')
          }
          break
        }
        case 'form_submitted': {
          if (config.form_id) {
            const f = this.forms.find(x => x.id === config.form_id)
            items.push(f ? (f.name || f.title) : 'Formulario #' + config.form_id)
          } else {
            items.push('Qualquer formulario')
          }
          break
        }
        case 'tag_added':
          if (config.tag_name) items.push(config.tag_name)
          break
        case 'status_changed': {
          if (config.status_id) {
            const s = this.statuses.find(x => x.id === config.status_id)
            items.push(s ? s.name : 'Status #' + config.status_id)
          } else {
            items.push('Qualquer status')
          }
          break
        }
        case 'scheduled': {
          if (config.status_id) {
            const s = this.statuses.find(x => x.id === config.status_id)
            items.push(s ? ('Segmento: ' + s.name) : 'Segmento status #' + config.status_id)
          } else {
            items.push('Definir status')
          }
          break
        }
        case 'send_email': {
          if (config.email_template_id) {
            const t = this.emailTemplates.find(x => x.id === config.email_template_id)
            items.push(t ? (t.subject || t.name) : 'Template #' + config.email_template_id)
          } else {
            items.push('Selecionar template')
          }
          break
        }
        case 'send_whatsapp': {
          if ((config.mode || 'template') === 'text') {
            const txt = (config.text || '').trim()
            items.push(txt ? 'Texto: ' + (txt.length > 40 ? txt.slice(0, 40) + '…' : txt) : 'Texto livre')
          } else if (config.wa_template_id) {
            const t = this.waTemplates.find(x => x.id === config.wa_template_id)
            items.push(t ? (t.name || t.title) : 'Template #' + config.wa_template_id)
          } else {
            items.push('Selecionar template')
          }
          break
        }
        case 'wait_time':
          if (config.amount) {
            items.push(config.amount + ' ' + this.unitLabel(config.unit))
          }
          if (config.skip_weekends) items.push('Pula fds')
          if (config.business_hours_only) items.push('Horario comercial')
          break
        case 'wait_date':
          if (config.date) items.push(config.date)
          break
        case 'condition':
          if (config.field) {
            items.push((config.field + ' ' + (config.operator || '') + ' ' + (config.value || '')).trim())
          }
          break
        case 'ab_test':
          items.push('A: ' + (config.variant_a_pct || 50) + '%')
          items.push('B: ' + (config.variant_b_pct || 50) + '%')
          break
        case 'add_tag':
        case 'remove_tag':
          if (config.tag_name) items.push(config.tag_name)
          break
        case 'update_status': {
          if (config.field_value) {
            const s = this.statuses.find(x => String(x.id) === String(config.field_value))
            items.push('Status → ' + (s ? s.name : config.field_value))
          }
          break
        }
        case 'update_field':
          if (config.field_name) items.push(config.field_name)
          if (config.field_value) items.push('→ ' + config.field_value)
          break
        case 'internal_note':
          if (config.note_text) {
            items.push(config.note_text.length > 40 ? config.note_text.substring(0, 40) + '...' : config.note_text)
          }
          break
        case 'send_follow_up_action': {
          if (config.follow_up_rule_id) {
            const r = this.followUpRules.find(x => x.id === config.follow_up_rule_id)
            items.push(r ? r.name : 'Regra #' + config.follow_up_rule_id)
            if (config.send_email === false) items.push('sem e-mail')
          } else {
            items.push('Selecionar regra')
          }
          break
        }
        case 'go_to_journey': {
          if (config.target_journey_id) {
            const j = this.journeysList.find(x => x.id === config.target_journey_id)
            items.push(j ? j.name : 'Jornada #' + config.target_journey_id)
          }
          break
        }
        case 'end_journey':
          if (config.reason) {
            const reasons = { completed: 'Concluida', goal_reached: 'Objetivo alcancado', timeout: 'Timeout', manual: 'Manual' }
            items.push(reasons[config.reason] || config.reason)
          }
          break
        case 'email_engagement':
          if (config.rule_type) {
            const rules = { opened: 'Abriu email', clicked: 'Clicou email' }
            items.push(rules[config.rule_type] || config.rule_type)
          }
          break
        case 'wa_engagement':
          if (config.rule_type) {
            const rules = { replied: 'Respondeu WA', delivered: 'Recebeu WA' }
            items.push(rules[config.rule_type] || config.rule_type)
          }
          break
      }
      return items
    }
  }
}
</script>

<style scoped>
.fnode {
  min-width: 340px;
  width: max-content;
  max-width: 100%;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
}
.fnode:hover {
  border-color: #d0d5dd;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}
.fnode.selected {
  border-color: #556ee6;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.15);
}
.fnode-nested {
  min-width: 0;
  width: 100%;
  border-radius: 10px;
}

.fnode-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #f3f4f6;
}
.fnode-nested .fnode-head {
  padding: 8px 10px;
  gap: 7px;
}

.node-drag-handle {
  cursor: grab;
  color: #d0d5dd;
  font-size: 16px;
  padding: 2px;
  transition: color 0.15s;
  flex-shrink: 0;
}
.node-drag-handle:hover { color: #556ee6; }
.node-drag-handle:active { cursor: grabbing; }

.fnode-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  flex-shrink: 0;
  color: #fff;
}
.fnode-nested .fnode-icon {
  width: 26px;
  height: 26px;
  font-size: 13px;
  border-radius: 7px;
}

.fnode-info {
  flex: 1;
  min-width: 0;
}
.fnode-name {
  font-size: 13px;
  font-weight: 600;
  color: #344054;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fnode-nested .fnode-name { font-size: 12.5px; }
.fnode-type {
  font-size: 11px;
  color: #9ca3af;
  display: block;
  margin-top: 1px;
}
.fnode-nested .fnode-type { font-size: 10.5px; }

.btn-node-action {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #9ca3af;
  cursor: pointer;
  font-size: 15px;
  transition: all 0.15s;
  opacity: 0;
  flex-shrink: 0;
}
.fnode:hover > .fnode-head > .btn-node-action { opacity: 1; }
.btn-node-action:hover { color: #dc2626; background: #fee2e2; }

.fnode-body {
  padding: 8px 12px;
  border-bottom: 1px solid #f3f4f6;
}
.fnode-nested .fnode-body { padding: 6px 10px; }
.fnode-config-items {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.cfg-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  background: #f3f4f6;
  border-radius: 6px;
  font-size: 12px;
  color: #495057;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fnode-nested .cfg-pill { font-size: 11.5px; }

.fnode-branches {
  display: flex;
  gap: 8px;
  padding: 10px;
  border-bottom: 1px solid #f3f4f6;
  align-items: flex-start;
}
.fbranch {
  flex: 1;
  min-width: 240px;
  border-radius: 8px;
  padding: 8px;
  font-size: 11px;
  transition: all 0.15s;
  display: flex;
  flex-direction: column;
}
.fnode-nested .fbranch {
  min-width: 220px;
  padding: 7px;
}
.fbranch-yes {
  background: #ecfdf5;
  border: 1px solid #d1fae5;
}
.fbranch-no {
  background: #fef2f2;
  border: 1px solid #fee2e2;
}
.fbranch-head {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 7px;
  font-weight: 600;
  font-size: 11.5px;
}
.fbranch-yes .fbranch-head { color: #059669; }
.fbranch-no .fbranch-head { color: #dc2626; }
.fbranch-count {
  font-weight: 400;
  color: #9ca3af;
  margin-left: auto;
}
.fbranch-nodes {
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
}
.fbranch-nodes > * + * {
  position: relative;
}
.fbranch-nodes > * + *::before {
  content: '';
  position: absolute;
  top: -6px;
  left: 50%;
  width: 2px;
  height: 6px;
  background: rgba(148, 163, 184, 0.35);
  transform: translateX(-50%);
}
.fbranch-add {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 7px 6px;
  font-size: 11.5px;
  color: #9ca3af;
  border: 1.5px dashed #d0d5dd;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  background: rgba(255, 255, 255, 0.5);
}
.fbranch-add i { font-size: 13px; }
.fbranch-add:hover {
  border-color: #556ee6;
  color: #556ee6;
  background: rgba(85, 110, 230, 0.06);
}

.fnode-stats {
  display: flex;
  gap: 16px;
  padding: 8px 14px;
  background: rgba(0, 0, 0, 0.015);
}
.fstat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #6b7280;
}
.fstat-emoji {
  font-size: 12px;
  line-height: 1;
}
.fstat-value {
  font-weight: 600;
  color: #495057;
}
.fstat-label {
  color: #9ca3af;
}
</style>
